<template>
	<div class="proposal-viewer">
		<div class="proposal-toolbar d-print-none" v-if="proposal">
			<div class="proposal-toolbar__identity">
				<span class="proposal-toolbar__icon"><portal-icon name="file-check" :size="20" /></span>
				<span class="proposal-toolbar__title">{{ proposal.coverPage.heading }}</span>
			</div>
			<div class="proposal-toolbar__actions">
			<comments-menu
				v-if="proposal"
				:comments="proposal.comments"
				:proposal-id="proposal.id"
				:client-id="proposal.clientId"
				:proposal-service="proposalService"
				:client-mode="true"
				:viewed-timestamps="proposal.viewedTimestamps"
			></comments-menu>
			<button v-if="$vuetify.breakpoint.smAndUp" class="proposal-toolbar__button" type="button" @click="print()">
				<portal-icon name="printer" :size="18" />
				<span>{{ $t('proposals.print-save-pdf') }}</span>
			</button>
			<div class="proposal-toolbar__button proposal-toolbar__language">
				<language-switcher
					:label="$t('settings.language')"
					:children="countries.getCountriesTranslate()"
					icon="translate"
					@selected="switchLocale($event)"
				></language-switcher>
			</div>
			</div>
		</div>
		<div v-if="!isReady" class="proposal-loading">
			<v-progress-circular indeterminate color="primary" :size="34" :width="3" />
		</div>
		<div class="proposal-document" v-else>
		<proposal-renderer
			:proposal="proposal"
			:account="account"
			:proposal-service="proposalService"
			:edit-mode="false"
			:pod-service="null"
			:client-service="null"
			:need-save="false"
			:saving="false"
			:client-mode="true"
			:is-locked="isLocked"
			:card-payment-failed="cardPaymentFailed"
			@deliverable-selected="deliverableSelected($event)"
			@client-signed="clientFinalized($event)"
			@client-accepted="clientFinalized($event)"
		></proposal-renderer>
		</div>
	</div>
</template>

<script>
	import PubNub from 'pubnub';
	import qs from 'qs';
	import ProposalViewerService from './ProposalViewerService';
	import ProposalRenderer from '@/modules/proposal/proposal-renderer/ProposalRenderer';
	import CommentsMenu from '@/modules/proposal/proposal-renderer/CommentsMenu';
	import LanguageSwitcher from '@/components/LanguageSwitcher';
	import Countries from '@/modules/utils/Countries';
	import PortalIcon from '@/components/PortalIcon';

	export default {
		name: 'ProposalViewer.vue',
		components: { CommentsMenu, ProposalRenderer, LanguageSwitcher, PortalIcon },

		props: ['id'],

		data: function() {
			return {
				countries: new Countries(),
				httpClient: null,
				token: null,
				docId: null,
				contactId: null,
				proposal: null,
				account: null,
				isReady: false,
				proposalService: null,
				selectedPackageIds: [],
				theirSignature: null,
				pubNub: null,
				cardPaymentFailed: false,
			};
		},

		mounted: function() {
			this.$vuetify.theme.dark = false;
			setTimeout(() => (this.$vuetify.theme.dark = false), 50);

			if (this.id) {
				this.token = this.id;
				this.docId = this.id;
				this.proposalService = new ProposalViewerService(this.token, this.$store.getters.getPodUrl);
			} else {
				let queryString = qs.parse(self.location.search.slice(1));
				let request = JSON.parse(atob(queryString.token.split('.')[0]));
				this.token = queryString.token;
				this.docId = request.docId;
				this.contactId = request.contactId;
				this.proposalService = new ProposalViewerService(this.token, request.podUrl);
			}

			this.proposalService
				.getProposal()
				.then((res) => {
					this.proposal = res.data;
					this.account = this.proposal.accountMini;
					this.$store.commit('setAccountId', this.proposal.accountId);
					this.$store.commit('setUserId', this.contactId ? this.contactId : this.docId);
					this.proposalService.setLastViewed();
					this.isReady = true;
					this.setupPubNub();
				})
				.catch((err) => {
					console.log(err);
					this.notFound = true;
					this.$store.commit('error', err.response.data.message);
				});
		},

		beforeDestroy() {
			this.proposalService.setLastViewed();
		},

		methods: {
			print: function() {
				window.print();
			},

			setupPubNub: function() {
				let uuid = this.docId;
				let authKey = this.contactId ? this.contactId : this.docId;
				let channel = 'c-' + this.proposal.accountId + '.p-' + this.proposal.id;

				this.pubNub = new PubNub({
					subscribeKey: process.env.VUE_APP_PUBNUB_SUBSCRIBE,
					publishKey: process.env.VUE_APP_PUBNUB_PUBLISH,
					uuid: uuid,
					authKey: authKey,
					ssl: true,
				});

				this.pubNub.addListener({
					message: this.onMessage,
				});

				this.pubNub.subscribe({
					channels: [channel],
				});
			},

			onMessage: function(msg) {
				this.$store.state.eventBus.$emit(msg.channel, msg);
			},

			deliverableSelected: function(deliverable) {
				if (this.proposal.allowMultipleDeliverables) {
					if (deliverable.selectedPackage) {
						this.selectedPackageIds.push(deliverable.id);
					} else {
						let ix = this.selectedPackageIds.findIndex((p) => p === deliverable.id);
						if (ix > -1) {
							this.selectedPackageIds.splice(ix, 1);
						}
					}
				} else {
					this.selectedPackageIds.splice(0, this.selectedPackageIds.length);
					this.selectedPackageIds.push(deliverable.id);
				}
			},

			clientFinalized: function(payload) {
				let block = payload.block;

				if (this.requirePackageSelection && this.selectedPackageIds.length === 0) {
					this.$store.commit('error', this.$t('error.package-not-selected'));
				} else {
					let result = {
						selectedPackageIds: this.selectedPackageIds,
						cardTokenId: payload.cardTokenId,
						upFrontAmount: payload.upFrontAmount,
					};

					if (block.blockType === 'SignatureBlock') {
						result.signature = block.them;
					} else {
						result.clientAccept = true;
					}

					this.$store.commit('startLoading');
					this.proposalService
						.executeProposal(result)
						.then((res) => {
							this.cardPaymentFailed = false;
							this.proposal = res.data;
						})
						.catch((err) => {
							if (result.cardTokenId) {
								this.cardPaymentFailed = true;
							}
							this.$store.commit('error', err.response.data.message);
						})
						.finally(() => this.$store.commit('stopLoading'));
				}
			},

			switchLocale: function(lang) {
				localStorage.setItem('lang', lang);
				this.$store.state.selectLang = lang;
				this.$store.state.eventBus.$emit('lang-changed', lang);
				window.location.reload();
				console.log('Im hit!');
			},
		},

		computed: {
			signatureBlock: function() {
				for (let i = 0; i < this.proposal.contentBlocks.length; i++) {
					if (this.proposal.contentBlocks[i].blockType === 'SignatureBlock') {
						return this.proposal.contentBlocks[i];
					}
				}

				return null;
			},

			acceptBlock: function() {
				for (let i = 0; i < this.proposal.contentBlocks.length; i++) {
					if (this.proposal.contentBlocks[i].blockType === 'AcceptBlock') {
						return this.proposal.contentBlocks[i];
					}
				}

				return null;
			},

			requirePackageSelection: function() {
				if (this.proposal.forceAllDeliverables) {
					return false;
				}

				let deliverableCount = 0;
				for (let i = 0; i < this.proposal.contentBlocks.length; i++) {
					if (this.proposal.contentBlocks[i].blockType === 'Deliverables') {
						deliverableCount = deliverableCount + 1;
					}
				}

				return deliverableCount > 1;
			},

			isLocked: function() {
				let signature = this.signatureBlock;
				if (signature) {
					return !this.$validations.isEmpty(signature.them.signature);
				} else if (this.acceptBlock) {
					return this.acceptBlock.accepted ? true : false;
				} else {
					return false;
				}
			},
		},
	};
</script>

<style lang="scss">
	* {
		::-webkit-scrollbar {
			display: unset !important;
		}
	}

	.proposal-viewer {
		width: 100%;
		min-height: var(--vh);
		padding: 20px;
		background: var(--v-gray_10-base);
	}

	.proposal-toolbar {
		position: sticky;
		top: 16px;
		z-index: 210;
		max-width: 1120px;
		min-height: 68px;
		margin: 0 auto 18px;
		padding: 10px 12px 10px 16px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		background: rgba(255, 255, 255, 0.94);
		border: 1px solid var(--v-gray_30-base);
		border-radius: var(--brand-surface-radius, 20px);
		box-shadow: 0 14px 40px rgba(22, 24, 35, 0.08);
		backdrop-filter: blur(16px);

		&__identity,
		&__actions {
			display: flex;
			align-items: center;
			gap: 10px;
			min-width: 0;
		}

		&__icon {
			width: 40px;
			height: 40px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			border-radius: 13px;
			color: var(--v-primary-base);
			background: var(--v-primary_10-base);
		}

		&__title {
			max-width: 400px;
			font-size: 17px;
			font-weight: 650;
			color: var(--v-gray_90-base);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&__button {
			min-height: 42px;
			padding: 0 15px;
			border: 1px solid var(--v-gray_30-base);
			border-radius: var(--brand-button-radius, 13px);
			background: var(--v-white-base);
			color: var(--v-gray_80-base);
			display: inline-flex;
			align-items: center;
			gap: 8px;
			font-size: 14px;
			font-weight: 600;
		}

		&__button:hover {
			border-color: var(--v-gray_40-base);
			background: var(--v-gray_10-base);
		}
	}

	.proposal-document {
		max-width: 1120px;
		margin: 0 auto;
		overflow: hidden;
		border-radius: var(--brand-surface-radius, 22px);
		background: var(--v-white-base);
		box-shadow: 0 16px 48px rgba(22, 24, 35, 0.07);
	}

	.proposal-loading {
		min-height: 60vh;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	@media (max-width: 600px) {
		.proposal-viewer {
			padding: 0;
		}

		.proposal-toolbar {
			top: 0;
			margin: 0;
			min-height: 64px;
			border-width: 0 0 1px;
			border-radius: 0;
			box-shadow: 0 8px 24px rgba(22, 24, 35, 0.06);

			&__title {
				max-width: 38vw;
				font-size: 15px;
			}

			&__language {
				padding: 0 8px;
			}
		}

		.proposal-document {
			border-radius: 0;
			box-shadow: none;
		}
	}

	@media print {
		.proposal-viewer {
			padding: 0;
			background: white;
		}

		.proposal-document {
			max-width: none;
			box-shadow: none;
		}
	}
</style>
